var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, memo } from 'react';
import styled from '@mui/material/styles/styled';
import { FormControl, InputLabel, FormHelperText, inputLabelClasses, } from '@mui/material';
import Input from '../Input';
import Select from '../Select';
import { chineseLength } from '../../utils';
var FormItemRoot = styled(FormControl)(function () { return ({
    display: 'block',
    height: '84px',
}); });
var LabelRoot = styled(InputLabel)(function (_a) {
    var _b, _c, _d;
    var variant = _a.variant, size = _a.size;
    return __assign(__assign((_b = { lineHeight: 1, zIndex: 1, top: 0, color: 'rgba(0, 0, 0, 0.26)' }, _b["&.".concat(inputLabelClasses.disabled)] = {
        color: 'rgba(0, 0, 0, 0.26)',
    }, _b), (variant === 'outlined' && (_c = {},
        _c["&.".concat(inputLabelClasses.shrink)] = {
            transform: "translate(14px, -5px) scale(0.75)",
            top: -2,
        },
        _c))), (variant === 'outlined' &&
        size === 'small' && (_d = {
            top: '1px'
        },
        _d["&.".concat(inputLabelClasses.root)] = {
            fontSize: '14px',
        },
        _d["&.".concat(inputLabelClasses.shrink)] = {
            transform: 'translate(14px, -7px) scale(0.75)',
            fontSize: '16px !important',
        },
        _d)));
});
var HelperTextRoot = styled(FormHelperText)({
    display: 'flex',
    alignItems: 'center',
    marginRight: 8
});
var Label = function (props) {
    var children = props.children, labelProps = __rest(props, ["children"]);
    if (!children)
        return null;
    return (React.createElement(LabelRoot, __assign({}, labelProps), children));
};
var Helper = function (props) {
    var childrenProp = props.children, extra = props.extra, max = props.max, length = props.length, helperTextProps = __rest(props, ["children", "extra", "max", "length"]);
    var children = childrenProp;
    if (!children) {
        children = extra;
    }
    var count = useMemo(function () {
        if (max === undefined || typeof max !== 'number') {
            return null;
        }
        return (React.createElement("span", null,
            length,
            "/",
            max));
    }, [max, length]);
    return (React.createElement(HelperTextRoot, __assign({}, helperTextProps),
        React.createElement("span", { style: { flex: 1 } }, children || extra),
        count));
};
var FormItem = React.forwardRef(function (props, ref) {
    var options = props.options, SelectProps = props.SelectProps, name = props.name, label = props.label, error = props.error, max = props.max, extra = props.extra, length = props.length, LabelPropsProp = props.InputLabelProps, HelperPropsProp = props.HelperProps, childrenProp = props.children, value = props.value, inputProps = props.inputProps, InputProps = props.InputProps, inputRef = props.inputRef, variant = props.variant, clear = props.clear, onClear = props.onClear, autoComplete = props.autoComplete, _a = props.autoFocus, autoFocus = _a === void 0 ? false : _a, defaultValue = props.defaultValue, _b = props.fullWidth, fullWidth = _b === void 0 ? true : _b, maxRows = props.maxRows, minRows = props.minRows, _c = props.multiline, multiline = _c === void 0 ? false : _c, onBlur = props.onBlur, onChange = props.onChange, onFocus = props.onFocus, placeholder = props.placeholder, rows = props.rows, endAdornment = props.endAdornment, readOnly = props.readOnly, size = props.size, type = props.type, _d = props.placeholderIsValue, placeholderIsValue = _d === void 0 ? false : _d, FormControlProps = __rest(props, ["options", "SelectProps", "name", "label", "error", "max", "extra", "length", "InputLabelProps", "HelperProps", "children", "value", "inputProps", "InputProps", "inputRef", "variant", "clear", "onClear", "autoComplete", "autoFocus", "defaultValue", "fullWidth", "maxRows", "minRows", "multiline", "onBlur", "onChange", "onFocus", "placeholder", "rows", "endAdornment", "readOnly", "size", "type", "placeholderIsValue"]);
    var htmlFor = "".concat(name || '', "-htmlFor");
    var statusError = useMemo(function () {
        if (error === undefined || error === false) {
            return false;
        }
        return true;
    }, [error]);
    var children = childrenProp;
    if (childrenProp == null) {
        var InputElement = (React.createElement(Input, __assign({ name: name, type: type, size: size, clear: clear, label: label, error: statusError, value: value, variant: variant, "aria-describedby": htmlFor, autoComplete: autoComplete, autoFocus: autoFocus, defaultValue: defaultValue, fullWidth: fullWidth, multiline: multiline, rows: rows, maxRows: maxRows, minRows: minRows, inputRef: inputRef, placeholder: placeholder, endAdornment: endAdornment, readOnly: readOnly, inputProps: inputProps, onBlur: onBlur, onChange: onChange, onFocus: onFocus, onClear: onClear }, InputProps)));
        if (options) {
            children = React.createElement(Select, __assign({ input: InputElement, value: value, options: options }, SelectProps));
        }
        else {
            children = InputElement;
        }
    }
    var valLength = useMemo(function () {
        if (max > 0 && (value && typeof value === 'string')) {
            return chineseLength(value);
        }
        return 0;
    }, [value]);
    var helperText = useMemo(function () {
        if (typeof error === 'boolean') {
            return extra;
        }
        return error;
    }, [error]);
    return (React.createElement(FormItemRoot, __assign({ ref: ref, variant: variant, error: statusError }, FormControlProps),
        React.createElement(Label, __assign({ variant: variant, htmlFor: htmlFor, size: size }, LabelPropsProp, (placeholderIsValue && { shrink: true })), label),
        children,
        React.createElement(Helper, __assign({ max: max, length: valLength, extra: extra, error: statusError }, HelperPropsProp), helperText)));
});
FormItem.defaultProps = {
    length: 0,
    variant: 'outlined',
    fullWidth: true,
};
export default memo(FormItem, function (prev, next) {
    if (next.shouldMemoUpdate) {
        return next.shouldMemoUpdate(prev, next);
    }
    return false;
});
